/**
 * Phase HR-002 polish — shared mobile/tablet styles for HR floor pages.
 *
 * Extracted so Daily Attendance, My To-Do, and Worker Allocation all share
 * the same vocabulary: roster cards, status chips, threshold tiles, sticky
 * bottom bars, employee avatars, motion timings, reduced-motion guards.
 *
 * One <style> tag is injected per page-root class. Scoped to the class so
 * the rules can't leak. All animations respect prefers-reduced-motion.
 *
 * Doc thresholds (Spinning Mill HR & Workforce Study System §6) drive the
 * .threshold-tile--{red|amber|green} variants below.
 */
import { useEffect } from 'react'

const HR_POLISH_CSS = `
/* ── Page root ─────────────────────────────────────────────────────── */
.hr-mobile-root {
  padding: 16px;
  max-width: 1400px;
  margin: 0 auto;
  /* Reserve bottom padding for the sticky action bar on mobile */
  padding-bottom: 80px;
}
@media (min-width: 992px) {
  .hr-mobile-root { padding-bottom: 16px; }
}

/* ── Roster / employee card ────────────────────────────────────────── */
.hr-roster-card {
  border-radius: 12px;
  background: #fff;
  border: 1.5px solid #f0f0f0;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: transform 180ms ease-out,
              border-color 180ms ease-out,
              box-shadow 180ms ease-out,
              background 180ms ease-out;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.hr-roster-card:hover {
  border-color: #1677ff;
  box-shadow: 0 2px 8px rgba(22, 119, 255, 0.10);
}
.hr-roster-card:active {
  transform: scale(0.99);
}
.hr-roster-card--dirty {
  border-color: #fa8c16;
  background: linear-gradient(180deg, #fffbe6 0%, #fff 65%);
}
.hr-roster-card--present {
  border-color: #b7eb8f;
  background: linear-gradient(180deg, #f6ffed 0%, #fff 65%);
}
.hr-roster-card--absent {
  border-color: #ffa39e;
  background: linear-gradient(180deg, #fff2f0 0%, #fff 65%);
}
.hr-roster-card--leave {
  border-color: #91caff;
  background: linear-gradient(180deg, #e6f4ff 0%, #fff 65%);
}
.hr-roster-card--half {
  border-color: #ffd591;
  background: linear-gradient(180deg, #fff7e6 0%, #fff 65%);
}

/* ── Employee avatar ───────────────────────────────────────────────── */
.hr-avatar {
  width: 44px; height: 44px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-weight: 600; font-size: 15px;
  color: #fff;
  background: linear-gradient(135deg, #1677ff 0%, #0958d9 100%);
  letter-spacing: 0.5px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
.hr-avatar img {
  width: 100%; height: 100%;
  object-fit: cover;
}
.hr-avatar--md { width: 36px; height: 36px; font-size: 13px; }
.hr-avatar--sm { width: 28px; height: 28px; font-size: 11px; }

/* ── Status chip (tap to cycle) ────────────────────────────────────── */
.hr-status-chip {
  min-width: 92px;
  height: 36px;
  border-radius: 18px;
  padding: 0 14px;
  display: inline-flex; align-items: center; justify-content: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: transform 180ms cubic-bezier(0.34, 1.56, 0.64, 1),
              background 180ms ease-out,
              box-shadow 180ms ease-out;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  flex-shrink: 0;
}
.hr-status-chip:active { transform: scale(0.92); }
.hr-status-chip--present  { background: #52c41a; color: #fff; }
.hr-status-chip--absent   { background: #ff4d4f; color: #fff; }
.hr-status-chip--half     { background: #fa8c16; color: #fff; }
.hr-status-chip--leave    { background: #1677ff; color: #fff; }
.hr-status-chip--compoff  { background: #722ed1; color: #fff; }
.hr-status-chip--weeklyoff{ background: #8c8c8c; color: #fff; }
.hr-status-chip--pending  { background: #f0f0f0; color: #595959; border: 1px dashed #bfbfbf; }

/* ── Threshold KPI tile (doc §6 colors) ────────────────────────────── */
.threshold-tile {
  border-radius: 10px;
  padding: 12px 14px;
  text-align: center;
  border: 1.5px solid transparent;
  transition: background 200ms ease-out, border-color 200ms ease-out;
}
.threshold-tile--green {
  background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
  border-color: #b7eb8f;
}
.threshold-tile--amber {
  background: linear-gradient(135deg, #fffbe6 0%, #fff1b8 100%);
  border-color: #ffe58f;
}
.threshold-tile--red {
  background: linear-gradient(135deg, #fff2f0 0%, #ffccc7 100%);
  border-color: #ffa39e;
  animation: tile-flash 320ms cubic-bezier(0.36, 0.07, 0.19, 0.97);
}
.threshold-tile--neutral {
  background: #fafafa;
  border-color: #e8e8e8;
}
.threshold-tile__value {
  font-size: 26px;
  font-weight: 700;
  font-family: 'Inter', -apple-system, 'Segoe UI', sans-serif;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  margin: 2px 0;
}
.threshold-tile__label {
  font-size: 10px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #8c8c8c;
  font-weight: 500;
}
.threshold-tile--green .threshold-tile__value  { color: #389e0d; }
.threshold-tile--amber .threshold-tile__value  { color: #d48806; }
.threshold-tile--red   .threshold-tile__value  { color: #cf1322; }
.threshold-tile--neutral .threshold-tile__value { color: #595959; }

@keyframes tile-flash {
  10%, 90% { transform: translateX(-1px); }
  20%, 80% { transform: translateX(1px); }
  30%, 50%, 70% { transform: translateX(-2px); }
  40%, 60% { transform: translateX(2px); }
}

/* ── Critical-post vacancy banner (doc top-12) ─────────────────────── */
.critical-post-card {
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #fff2f0 0%, #ffe7e7 100%);
  border: 1.5px solid #ffa39e;
}
.critical-post-card--ok {
  background: linear-gradient(135deg, #f6ffed 0%, #e8ffe0 100%);
  border-color: #b7eb8f;
}
.critical-post-card__icon {
  font-size: 22px;
}

/* ── Sticky bottom action bar (mobile only) ────────────────────────── */
.hr-sticky-actions {
  position: fixed;
  /* On mobile the AppLayout renders a BottomTabBar (~60-64px tall) at
     bottom:0 with z-index 500. If we leave this bar at bottom:0 too, the
     tab bar covers it and the operator never sees the Save button. Stack
     the save bar ABOVE the tab bar by clearing both the tab bar height
     AND the iPhone home-indicator safe-area inset. */
  bottom: calc(60px + env(safe-area-inset-bottom, 0px));
  left: 0; right: 0;
  /* z-index above the bottom tab bar (500) so even if they ever overlap
     during a scroll/animation, save wins. */
  z-index: 600;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-top: 1px solid #e8e8e8;
  box-shadow: 0 -2px 16px rgba(0,0,0,0.08);
  display: flex;
  gap: 8px;
  margin-left: 0;
}
.hr-sticky-actions .ant-btn {
  flex: 1;
  height: 48px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 10px;
}
@media (min-width: 992px) {
  .hr-sticky-actions {
    /* Desktop has no bottom tab bar — go back to sticky-inline. */
    position: sticky;
    bottom: 16px;
    margin: 16px 0 0;
    border-radius: 12px;
    border: 1px solid #e8e8e8;
    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
    z-index: 30;
  }
}

/* Reserve enough bottom padding inside hr-mobile-root so the last card
   isn't hidden behind the stacked Save bar + Bottom Tab Bar. The pages
   already set padding-bottom: 80px, bump to clear both bars. */
@media (max-width: 991px) {
  .hr-mobile-root { padding-bottom: 152px; }
}

/* ── Filter chip row ───────────────────────────────────────────────── */
.hr-filter-chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
}
.hr-filter-chip {
  height: 34px;
  padding: 0 14px;
  border-radius: 17px;
  background: #fafafa;
  border: 1.5px solid #f0f0f0;
  font-size: 13px;
  font-weight: 500;
  color: #595959;
  cursor: pointer;
  transition: all 150ms ease-out;
  display: inline-flex; align-items: center;
  white-space: nowrap;
}
.hr-filter-chip:hover {
  border-color: #91caff;
  color: #1677ff;
}
.hr-filter-chip--active {
  background: #1677ff;
  border-color: #1677ff;
  color: #fff;
}

/* ── Save-success pulse ────────────────────────────────────────────── */
.hr-save-success {
  animation: hr-pulse-green 600ms ease-out;
}
@keyframes hr-pulse-green {
  0%   { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.6); }
  70%  { box-shadow: 0 0 0 16px rgba(82, 196, 26, 0); }
  100% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0); }
}

/* ── Reduced motion (WCAG / Apple HIG) ─────────────────────────────── */
@media (prefers-reduced-motion: reduce) {
  .hr-roster-card,
  .hr-status-chip,
  .threshold-tile,
  .hr-filter-chip {
    transition: none !important;
    animation: none !important;
  }
}
`

export function useHrMobilePolish() {
  useEffect(() => {
    const id = 'hr-mobile-polish-css'
    if (document.getElementById(id)) return
    const el = document.createElement('style')
    el.id = id
    el.textContent = HR_POLISH_CSS
    document.head.appendChild(el)
  }, [])
}

/** Threshold helpers — return the tile class based on doc-validated cutoffs. */
export type ThresholdResult = 'green' | 'amber' | 'red' | 'neutral'

export function attendancePctClass(pct: number | null): ThresholdResult {
  if (pct == null) return 'neutral'
  // Doc §6: Attendance %  >=96 green / 94-95 amber / <94 red
  if (pct >= 96) return 'green'
  if (pct >= 94) return 'amber'
  return 'red'
}
export function absenteeismPctClass(pct: number | null): ThresholdResult {
  if (pct == null) return 'neutral'
  // Doc §6: Absenteeism % <=4 green / 4-6 amber / >6 red
  if (pct <= 4) return 'green'
  if (pct <= 6) return 'amber'
  return 'red'
}
export function efficiencyPctClass(pct: number | null): ThresholdResult {
  if (pct == null) return 'neutral'
  // Doc §6: Worker efficiency >=90 green / 85-89 amber / <85 red
  if (pct >= 90) return 'green'
  if (pct >= 85) return 'amber'
  return 'red'
}
export function ppeCompliancePctClass(pct: number | null): ThresholdResult {
  if (pct == null) return 'neutral'
  // Doc §6: PPE compliance >=98 green / 95-97 amber / <95 red
  if (pct >= 98) return 'green'
  if (pct >= 95) return 'amber'
  return 'red'
}
export function criticalCertPctClass(pct: number | null): ThresholdResult {
  if (pct == null) return 'neutral'
  // Doc §6: Critical-role certification — must be 100% for critical posts
  if (pct >= 100) return 'green'
  if (pct >= 95) return 'amber'
  return 'red'
}

/** Render initials for an employee whose photo is missing. */
export function employeeInitials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}
